/**
 * Viewpoint stage-writing module.
 * Translates planner output into the serialized Jig camera shape and coordinate
 * system expected by stage payloads.
 */
import type { ViewpointVector3 } from '../generate-jig.types'
import type { ViewpointQuaternion } from './quaternion'

function roundValue(value: number): number {
  return parseFloat(value.toFixed(6))
}

function toJigPosition(position: ViewpointVector3): ViewpointVector3 {
  return {
    x: roundValue(-position.x),
    y: roundValue(position.y),
    z: roundValue(-position.z),
  }
}

function createViewpointId(): string {
  const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
  return template.replace(/[xy]/g, function (char: string) {
    const randomValue = Math.floor(Math.random() * 16)
    const value = char === 'x' ? randomValue : (randomValue & 0x3) | 0x8
    return value.toString(16)
  })
}

/**
 * Writes the final planned camera into the stage in the coordinate system and field
 * layout expected by the Jig payload.
 */
export function writeViewpointToStage(
  stage: any,
  cameraPosition: ViewpointVector3,
  rotation: ViewpointQuaternion,
  targetPosition: ViewpointVector3,
  distance: number
): void {
  const jigCameraPosition = toJigPosition(cameraPosition)
  const jigTargetPosition = toJigPosition(targetPosition)
  const viewpointId = createViewpointId()

  stage.Camera = {
    Viewpoints: {
      _items: [
        {
          ViewpointId: viewpointId,
          Active: true,
          Position: jigCameraPosition,
          Rotation: rotation,
          TargetPosition: jigTargetPosition,
          Distance: roundValue(distance),
        },
      ],
    },
  }

  stage.IsViewpointActiveInStage = true
  stage.ViewpointPosition = jigCameraPosition
  stage.ViewpointRotation = rotation
  stage.ViewpointTargetPosition = jigTargetPosition
  stage.ViewpointDistance = roundValue(distance)
}
